/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { I18nProvider, useT } from './i18n/i18n';
import { useApp } from './lib/store';
import { TabBar, type Tab } from './components/ui';
import OnboardingScreen from './screens/OnboardingScreen';
import HomeScreen from './screens/HomeScreen';
import TrackScreen from './screens/TrackScreen';
import HistoryScreen from './screens/HistoryScreen';
import GuideScreen from './screens/GuideScreen';
import SettingsScreen from './screens/SettingsScreen';
import PrivacyScreen from './screens/PrivacyScreen';

interface Nav {
  tab: Tab;
  go: (tab: Tab) => void;
  /** Return from Settings to the tab it was opened from. */
  back: () => void;
}
const NavCtx = createContext<Nav>({ tab: 'home', go: () => {}, back: () => {} });
export function useNav(): Nav {
  return useContext(NavCtx);
}

function Shell() {
  const { ready, device, settings } = useApp();
  const t = useT();
  const [tab, setTab] = useState<Tab>('home');
  // Settings is reachable from every tab; remember where it was opened
  // from so its back button returns there instead of always landing on Home.
  const [returnTab, setReturnTab] = useState<Tab>('home');
  const nav = useMemo<Nav>(
    () => ({
      tab,
      go: (next: Tab) => {
        if (next === 'settings' && tab !== 'settings') setReturnTab(tab);
        setTab(next);
      },
      back: () => setTab(tab === 'settings' ? returnTab : 'home'),
    }),
    [tab, returnTab],
  );

  // Apply theme, accent, and language to <html>.
  useEffect(() => {
    const root = document.documentElement;
    if (settings.theme === 'auto') delete root.dataset.theme;
    else root.dataset.theme = settings.theme;
    root.dataset.accent = settings.accent;
    root.lang = settings.locale;

    // Keep the browser/OS chrome in step with the chosen accent. The light
    // palette input is readable whatever theme is active, so the stylesheet
    // stays the single source of truth for the actual color values.
    const lightPrimary = getComputedStyle(root).getPropertyValue('--a-pri-l').trim();
    if (lightPrimary) {
      document
        .querySelector('meta[name="theme-color"][media*="light"]')
        ?.setAttribute('content', lightPrimary);
    }
  }, [settings.theme, settings.accent, settings.locale]);

  if (!ready) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-ink-soft">
        {t.common.loading}
      </div>
    );
  }

  if (!device?.onboarded) {
    return <OnboardingScreen />;
  }

  return (
    <NavCtx.Provider value={nav}>
      <div className="mx-auto min-h-dvh max-w-lg pb-24">
        {tab === 'home' && <HomeScreen />}
        {tab === 'track' && <TrackScreen />}
        {tab === 'history' && <HistoryScreen />}
        {tab === 'guide' && <GuideScreen />}
        {tab === 'settings' && <SettingsScreen />}
        {tab === 'privacy' && <PrivacyScreen />}
      </div>
      {tab !== 'settings' && tab !== 'privacy' && <TabBar tab={tab} onChange={setTab} />}
    </NavCtx.Provider>
  );
}

export default function App({ children }: { children?: ReactNode }) {
  const { settings } = useApp();
  return (
    <I18nProvider locale={settings.locale}>
      <Shell />
      {children}
    </I18nProvider>
  );
}
